import React from "react";
import { Button } from "@/components/ui/button";
import { Nfc, Database, Wifi, MapPin, BarChart3 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function AtlasHero({ onRequestDemo }) {
  const { t } = useI18n();
  return (
    <section id="top" data-testid="atlas-hero" className="relative overflow-hidden bg-slate-900">
      {/* Background video */}
      <video
        data-testid="hero-bg-video"
        className="absolute inset-0 w-full h-full object-cover"
        src="/atlas-hero.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster="/atlas-showcase.jpg"
        aria-hidden
      />
      {/* Dark gradient overlay for readability */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.65) 45%, rgba(15,23,42,0.35) 100%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-title"
              className="font-display font-extrabold text-white leading-[1.05] text-5xl sm:text-6xl lg:text-7xl drop-shadow-md tracking-tight"
            >
              {t("hero.title1")}
            </h1>
            <h2 className="font-display text-white/95 leading-[1.15] mt-4 text-2xl sm:text-3xl lg:text-[34px] font-medium drop-shadow max-w-xl">
              {t("hero.title2a")}{" "}
              <span className="font-extrabold text-white">
                {t("hero.title2b")}
              </span>
            </h2>
            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base sm:text-lg text-slate-100/85 max-w-xl"
            >
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
              <Button
                data-testid="hero-request-demo-btn"
                onClick={onRequestDemo}
                className="bg-[#6B46C1] hover:bg-[#553C9A] text-white rounded-lg px-6 h-12 text-[15px] font-semibold shadow-lg shadow-purple-900/40"
              >
                {t("cta.requestDemo")}
              </Button>
              <Button
                data-testid="hero-more-info-btn"
                variant="outline"
                onClick={() => {
                  const el = document.getElementById("soluciones");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="border-white/40 text-white hover:bg-white/10 rounded-lg px-6 h-12 text-[15px] font-semibold bg-white/5 backdrop-blur-sm"
              >
                {t("cta.learnMore")}
              </Button>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 relative h-[420px]">
            <FloatingIcon className="absolute top-6 left-16 atlas-float">
              <Nfc className="text-sky-400" size={26} />
            </FloatingIcon>
            <FloatingIcon className="absolute top-28 right-8 atlas-float-slow" delay="0.6s">
              <Database className="text-sky-400" size={26} />
            </FloatingIcon>
            <FloatingIcon className="absolute top-56 left-28 atlas-float" delay="1.1s">
              <MapPin className="text-sky-400" size={26} />
            </FloatingIcon>
            <FloatingIcon className="absolute bottom-10 right-20 atlas-float-slow" delay="0.3s">
              <Wifi className="text-sky-400" size={26} />
            </FloatingIcon>
            <FloatingIcon className="absolute bottom-24 left-8 atlas-float" delay="1.4s">
              <BarChart3 className="text-sky-400" size={26} />
            </FloatingIcon>
            <svg
              className="absolute inset-0 w-full h-full opacity-60"
              viewBox="0 0 400 420"
              fill="none"
              aria-hidden
            >
              <path
                d="M90 70 L320 170 M320 170 L170 260 M170 260 L300 370 M90 70 L110 260"
                stroke="#93c5fd"
                strokeWidth="1.5"
                strokeDasharray="3 5"
              />
            </svg>
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className="relative h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
      />
    </section>
  );
}

function FloatingIcon({ children, className = "", delay = "0s" }) {
  return (
    <div
      className={`h-14 w-14 rounded-full bg-white/95 backdrop-blur-md border border-sky-100 shadow-xl shadow-sky-900/30 flex items-center justify-center ${className}`}
      style={{ animationDelay: delay }}
    >
      {children}
    </div>
  );
}
